import type BoardView from '@models/board-view';
import GameState from '@services/core/GameState';

/**
 * MovementService - Handles player movement on the board
 *
 * Responsibilities:
 * - Move player step-by-step through nodes
 * - Update GameState position
 * - Update board view (node visual states: normal → passed → current)
 * - Emit events for each step and landing
 */
export class MovementService {
  private isMoving = false;

  constructor(
    private scene: Phaser.Scene,
    private gameState: GameState,
    private boardView: BoardView,
  ) {}

  /**
   * Move player by a number of steps
   * Updates node states: previous nodes become "passed", current node becomes "current"
   */
  public async movePlayer(steps: number, position: number): Promise<void> {
    if (this.isMoving) return;
    if (steps <= 0) return;

    this.isMoving = true;

    try {
      const currentPos = this.gameState.getPosition();
      const maxPos = this.boardView.model.length;
      const targetPos = Math.min(position, maxPos);

      for (let i = currentPos + 1; i < targetPos; i++) {
        await this.moveToNode(i);
        // Update game state and trigger passThrough visual effect
        this.gameState.passThrough(i);
        this.boardView.passThrough(i);
      }

      // Final landing (state + view sync)
      await this.setPosition(targetPos);
      this.boardView.landOn(targetPos);
      this.boardView.startCurrentNodeOutline();
    } finally {
      this.isMoving = false;
    }
  }

  /**
   * Move to a specific position
   */
  public async moveToPosition(position: number): Promise<void> {
    if (this.isMoving) {
      return;
    }

    const currentPos = this.gameState.getPosition();
    const steps = position - currentPos;

    if (steps > 0) {
      await this.movePlayer(steps, position);
    } else if (steps < 0) {
      // Backwards behavior depends on the rules:
      // If backwards should NOT lose rewards on the way:
      await this.setPosition(position);
    }
  }

  /**
   * Set position directly without animation
   */
  public async setPosition(position: number): Promise<void> {
    const maxPos = this.boardView.model.length;
    const clampedPos = Math.max(0, Math.min(position, maxPos));

    this.gameState.moveTo(clampedPos);
    this.boardView.setCurrentPosition(clampedPos);
  }

  public async flashNode(position: number, message: string): Promise<void> {
    await this.boardView.flashNode(position, message);
  }

  // Handles the animation when a move_forward tile is landed on
  public async animateMoveForward(
    fromPos: number,
    toPos: number,
    steps: number,
  ): Promise<void> {
    // Show the message on the tile before moving away from it
    await this.boardView.flashNode(
      fromPos,
      `Wow! You go forward ${steps} step${steps !== 1 ? 's' : ''}`,
    );

    // Walk the remaining steps just like a normal dice move
    for (let i = fromPos + 1; i < toPos; i++) {
      await this.moveToNode(i);
      this.gameState.passThrough(i);
      this.boardView.passThrough(i);
    }

    await this.setPosition(toPos);
    this.boardView.landOn(toPos);
    this.boardView.startCurrentNodeOutline();
  }

  public triggerRewardAnimation(): void {
    this.boardView.rewardConfirmedAt(this.gameState.getPosition());
  }

  /**
   * Hops the player forward to `moveBackPos`, shows the tooltip, then slides
   * the player back to `finalPos` once the tooltip dismisses.
   */
  public async animateMoveBack(
    moveBackPos: number,
    finalPos: number,
    message: string,
  ): Promise<void> {
    // Pause so the player can see their landed position before the hop
    await new Promise<void>((resolve) =>
      this.scene.time.delayedCall(500, resolve),
    );

    this.boardView.setCurrentPosition(moveBackPos);

    // Let the camera scroll settle before flashing
    await new Promise<void>((resolve) =>
      this.scene.time.delayedCall(350, resolve),
    );

    await this.boardView.flashNode(moveBackPos, message);

    this.boardView.setCurrentPosition(finalPos);
    this.boardView.resetNodesToDefault(finalPos + 1, moveBackPos);
    this.boardView.startCurrentNodeOutline();
  }

  /**
   * Move to a specific node with animation
   * Updates BoardView
   */
  private async moveToNode(index: number): Promise<void> {
    return new Promise((resolve) => {
      // View-only step
      this.boardView.setCurrentPosition(index);
      this.scene.time.delayedCall(500, () => resolve());
    });
  }
}
